// src/contexts/geral/ModalStackContext.js
import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const MODAL_BASE_Z_INDEX = 1050;
const MODAL_STACK_STEP = 10;

const ModalStackContext = createContext(null);

let modalSequence = 0;

const getNextModalId = () => {
  modalSequence += 1;
  return `modal-stack-${modalSequence}`;
};

const calculateLayers = (entries) => {
  let previousZIndex = MODAL_BASE_Z_INDEX - MODAL_STACK_STEP;

  return entries.map((entry) => {
    const zIndex = Math.max(
      entry.minimumZIndex,
      previousZIndex + MODAL_STACK_STEP
    );

    previousZIndex = zIndex;
    return { ...entry, zIndex, backdropZIndex: zIndex - 5 };
  });
};

export const ModalStackProvider = ({ children }) => {
  const [entries, setEntries] = useState([]);

  const registerModal = useCallback((id, minimumZIndex, blocksInteraction) => {
    setEntries((currentEntries) => {
      if (currentEntries.some((entry) => entry.id === id)) {
        return currentEntries;
      }

      return [...currentEntries, { id, minimumZIndex, blocksInteraction }];
    });
  }, []);

  const updateModal = useCallback((id, minimumZIndex, blocksInteraction) => {
    setEntries((currentEntries) => currentEntries.map((entry) => (
      entry.id === id && (
        entry.minimumZIndex !== minimumZIndex
        || entry.blocksInteraction !== blocksInteraction
      )
        ? { ...entry, minimumZIndex, blocksInteraction }
        : entry
    )));
  }, []);

  const unregisterModal = useCallback((id) => {
    setEntries((currentEntries) => currentEntries.filter((entry) => entry.id !== id));
  }, []);

  const layers = useMemo(() => calculateLayers(entries), [entries]);
  const value = useMemo(() => ({
    layers,
    registerModal,
    updateModal,
    unregisterModal,
  }), [layers, registerModal, updateModal, unregisterModal]);

  return (
    <ModalStackContext.Provider value={value}>
      {children}
    </ModalStackContext.Provider>
  );
};

export const useModalStack = (
  show,
  minimumZIndex = MODAL_BASE_Z_INDEX,
  { blocksInteraction = true } = {}
) => {
  const context = useContext(ModalStackContext);
  const registerModal = context?.registerModal;
  const updateModal = context?.updateModal;
  const unregisterModal = context?.unregisterModal;
  const idRef = useRef(null);

  if (!idRef.current) {
    idRef.current = getNextModalId();
  }

  const normalizedMinimumZIndex = Number(minimumZIndex) || MODAL_BASE_Z_INDEX;

  useLayoutEffect(() => {
    if (!show || !registerModal || !unregisterModal) return undefined;

    registerModal(idRef.current, normalizedMinimumZIndex, blocksInteraction);
    return () => unregisterModal(idRef.current);
  }, [show, registerModal, unregisterModal]);

  useLayoutEffect(() => {
    if (show && updateModal) {
      updateModal(idRef.current, normalizedMinimumZIndex, blocksInteraction);
    }
  }, [show, updateModal, normalizedMinimumZIndex, blocksInteraction]);

  const registeredLayer = context?.layers.find((entry) => entry.id === idRef.current);
  const topLayer = context ? context.layers[context.layers.length - 1] : null;
  const topBlockingLayer = context
    ? [...context.layers].reverse().find((entry) => entry.blocksInteraction !== false)
    : null;
  const currentTopZIndex = topLayer?.zIndex
    ?? (MODAL_BASE_Z_INDEX - MODAL_STACK_STEP);
  const provisionalZIndex = Math.max(
    normalizedMinimumZIndex,
    currentTopZIndex + MODAL_STACK_STEP
  );
  const zIndex = registeredLayer?.zIndex ?? provisionalZIndex;

  return {
    id: idRef.current,
    zIndex,
    backdropZIndex: registeredLayer?.backdropZIndex ?? zIndex - 5,
    isTopModal: show && blocksInteraction && (
      !context || !topBlockingLayer || topBlockingLayer.id === idRef.current
    ),
  };
};

export { MODAL_BASE_Z_INDEX, MODAL_STACK_STEP };
